import { FeatureId, teamMemberRoles } from "core";
import { useMemo } from "react";
import { useAuthContext } from "../auth/useAuthContext";
import {
  isFeatureSettingEnabled,
  useFeatureSettings,
} from "./useFeatureSettings";
import { useProject } from "./useProjects";

/**
 * Determines feature access based on user role and dynamic project settings from the database.
 *
 * This hook evaluates configurable feature permissions that can be toggled per project/role.
 * It combines user roles with project-specific feature settings to determine
 * what features the current user can access.
 *
 * For static, hardcoded system permissions, use `useIsGranted()` instead.
 */
export function useFeatureAccess() {
  const { identity } = useAuthContext();
  const { project, isLoadingProject } = useProject();
  const { settings } = useFeatureSettings({ role: identity.primaryRole });

  const permissions = useMemo(() => {
    /**
     * Roles which features access is controlled by owner of project.
     */
    const isRestrictedRole = !teamMemberRoles.includes(identity.primaryRole);
    const isAdvisorModeEnabled = !!project?.settings.advisorMode?.isEnabled;
    const featurePreCondition = isAdvisorModeEnabled;

    const hasFeatureAccess = (featureId: FeatureId) =>
      !isRestrictedRole ||
      (featurePreCondition && isFeatureSettingEnabled(featureId, settings));

    return {
      isRestrictedRole,
      isAdvisorModeEnabled,
      isDashboardAssistantEnabled: hasFeatureAccess(
        FeatureId.DashboardAssistant,
      ),
      isCreateModeEnabled: hasFeatureAccess(FeatureId.CreateMode),
      isBuildModeEnabled: hasFeatureAccess(FeatureId.BuildMode),
      isNetworkEnabled: hasFeatureAccess(FeatureId.Network),
      isWorkspaceEnabled: hasFeatureAccess(FeatureId.Workspace),
      isNotesEnabled: hasFeatureAccess(FeatureId.Notes),
      isTasksEnabled: hasFeatureAccess(FeatureId.Tasks),
      isZapierEnabled: hasFeatureAccess(FeatureId.Zapier),
    };
  }, [
    identity.primaryRole,
    project?.settings.advisorMode?.isEnabled,
    settings,
  ]);

  return {
    ...permissions,
    orgLogo: project?.logoUrl,
    isLoadingProject,
  };
}
